import { ChangeDetectionStrategy, Component, computed, input, OnInit, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, filter, finalize, tap } from 'rxjs/operators';

import { MessagePayload } from '@app/modules/message-center/models/message.model';
import { ConversationType, UserChat } from '@app/modules/message-center/models/chat.model';
import { MessageStatus, MessageType } from '@app/modules/message-center/shared/constants';
import { ChatService, MessageCenterAuthService } from '@app/modules/message-center/services';
import { oneOfValidator } from '@app/modules/message-center/shared/validators';
import { NotificationsService } from '@app/core/services';
import { Websocket } from '@app/core/services/websocket/websocket';
import { WsAction } from '@app/core/services/websocket/ws.models';
import { MCProfile } from '@app/modules/message-center/models/user.model';
import { UploadedFile } from '@app/shared/components/file-upload/models';
import { GroupType } from '@app/core/enums/message-center/group-type.enum';

@UntilDestroy()
@Component({
  selector: 'app-send-message-form',
  templateUrl: './send-message-form.component.html',
  styleUrls: ['./send-message-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SendMessageFormComponent implements OnInit {
  chat = input<UserChat>(null);
  threadMessageId = input<number>(null);
  sendImmediatelyWhenUploadFile = input<boolean>(false);
  chatWebsocket = input<Websocket>(null);
  threadIsOpened = input<boolean>(false);

  isSMSChat = computed<boolean>(() => this.chat()?.access_type === GroupType.SMS);

  form: FormGroup;
  loadedFiles: UploadedFile[] = [];
  isSending = signal<boolean>(false);
  isTyping = signal<boolean>(false);

  readonly MessageType = MessageType;

  private profile: MCProfile;

  readonly maxSMSMessageLength: number = 160;
  readonly messageTypeOptions = [
    { value: MessageType.sms, title: 'messageCenter.messageType_sms' },
    { value: MessageType.email, title: 'messageCenter.messageType_email' },
    { value: MessageType.internal, title: 'messageCenter.messageType_text' }
  ];
  readonly quickReplies: string[] = [
    'messageCenter.quickReply_gotIt',
    'messageCenter.quickReply_onMyWay',
    'messageCenter.quickReply_runningLate',
    'messageCenter.quickReply_thankYou'
  ];

  get isInternalMessage(): boolean {
    return this.form?.controls.channel.value === MessageType.internal;
  }

  get isSmsMessage(): boolean {
    return this.form?.controls.channel.value === MessageType.sms;
  }

  get isEmailMessage(): boolean {
    return this.form?.controls.channel.value === MessageType.email;
  }

  get files(): FormArray {
    return this.form.get('files') as FormArray;
  }

  constructor(
    private fb: FormBuilder,
    private chatService: ChatService,
    private notificationsService: NotificationsService,
    private messageCenterAuthService: MessageCenterAuthService,
  ) {
    this.profile = this.messageCenterAuthService.getProfile();
  }

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.form = this.fb.group({
      channel: [this.isSMSChat() ? MessageType.sms : MessageType.internal, [Validators.required]],
      subject: '',
      message: '',
      files: new FormBuilder().array([])
    }, {
      validators: [oneOfValidator('message', 'files')]
    });

    this.form.valueChanges.pipe(
      filter(() => this.chatWebsocket()?.isConnected),
      filter((value) => value.channel === MessageType.internal),
      tap(() => {
        if (!this.isTyping()) {
          this.sendTypingEvent(WsAction.user_typing_start);
          this.isTyping.set(true);
        }
      }),
      debounceTime(1000),
      untilDestroyed(this)
    ).subscribe(() => {
      if (this.isTyping()) {
        this.isTyping.set(false);
        this.sendTypingEvent(WsAction.user_typing_end);
      }
    });
  }

  addFiles(event: UploadedFile[]): void {
    this.loadedFiles = event;
    this.updateFilesInForm();

    if (this.sendImmediatelyWhenUploadFile()) {
      this.submit();
    }
  }

  private updateFilesInForm(): void {
    this.files.clear();
    this.loadedFiles.forEach(file => {
      this.files.push(this.addFileControl(file));
    });
  }

  private addFileControl(file: UploadedFile): FormGroup {
    return new FormBuilder().group({
      name: [file.name],
      file: [file.file]
    });
  }

  removeAttachedFile(index: number): void {
    this.loadedFiles.splice(index, 1);
    this.updateFilesInForm();
  }

  submit(): void {
    const payload: MessagePayload = this.getPayload();
    this.resetForm();
    
    this.isSending.set(true);
    this.chatService.sendMessage(payload).pipe(
      finalize(() => this.isSending.set(false)),
      untilDestroyed(this)
    ).subscribe({
      error: (error) => this.notificationsService.showError(error),
    });
  }

  private resetForm(): void {
    this.form.controls.message.setValue('');
    this.form.controls.subject.setValue('');
    this.files.clear();
    this.loadedFiles = [];
  }

  private sendTypingEvent(action: WsAction.user_typing_start | WsAction.user_typing_end): void {
    this.chatWebsocket().sendMessage({
      action,
      payload: {
        user_id: this.profile.id,
        chat_id: this.chat().id
      }
    });
  }

  private getPayload(): MessagePayload {
    const form = this.form.value;

    const recipients: number[] = this.chat().conversation_type === ConversationType.direct ?
      [this.chat().direct_to.id] : this.chat().members.map(client => client.id);

    const payload: MessagePayload = {
      chat: this.chat().id,
      recipients,
      status: MessageStatus.created,
      channel: form.channel,
      text: form.message,
      subject: form.subject,
      files_data: form.files.map(file => ({ file: file.file })),
    };

    if (this.threadMessageId()) {
      payload.reply_to = this.threadMessageId();
    }

    return payload;
  }

  changeMessageType(messageType: MessageType): void {
    this.form.controls.channel.setValue(messageType);

    const subjectControl = this.form.controls.subject;
    const messageControl = this.form.controls.message;

    subjectControl.setValue('');

    if (messageType === MessageType.sms) {
      this.files.clear();
      this.loadedFiles = [];
      messageControl.addValidators(Validators.maxLength(this.maxSMSMessageLength));
    } else {
      messageControl.clearValidators();
    }
    messageControl.updateValueAndValidity();

    if (messageType === MessageType.email) {
      subjectControl.setValidators(Validators.required);
    } else {
      subjectControl.clearValidators();
    }
    subjectControl.updateValueAndValidity();
  }
}
